import { useState, useEffect } from 'react';
import { useOutletContext } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import {
  FileText, Lock, ExternalLink, ChevronDown, ChevronUp, Upload,
  Brain, Plus, X, AlertCircle,
} from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';
import { supabase } from '../../lib/supabase';
import { getClients, getSessions, createSession, type CoachingClient, type CoachingSession } from '../../lib/coaching-sessions';
import { SessionIntelligencePanel } from '../../components/coach/SessionIntelligencePanel';

interface OutletCtx { isUnlocked: boolean; }
const WALKTHROUGH_URL = 'https://sessionintelligence.app/walkthrough';

const REPORT_SECTIONS = [
  'Session Summary',
  'Key Themes',
  'Limiting Beliefs',
  'Meta Model Patterns',
  'Emotional Language Signals',
  'Parts in Conflict',
  'Contradictions and Tensions',
  'Identity-Level Statements',
  'Values and Motivation Clues',
  'Avoidance or Resistance Patterns',
  'Breakthrough Zone',
  'Suggested Coach Questions',
  'Suggested Next Session Focus',
  'Important Cautions',
];

const SAMPLE_REPORT_PREVIEW: Record<string, string> = {
  'Session Summary': 'Sarah discussed ongoing difficulty saying no to people in her life — particularly colleagues and family. She expressed wanting more peace but feeling unable to set boundaries without guilt.',
  'Key Themes': 'Boundary setting, guilt, people-pleasing, values conflict between peace and approval.',
  'Limiting Beliefs': '"If I say no, they won\'t like me." "Keeping the peace is more important than my own needs."',
  'Breakthrough Zone': 'Saying no without guilt — Sarah may be able to reframe boundary-setting as an act of self-respect rather than rejection.',
  'Suggested Next Session Focus': 'Explore the difference between guilt as a moral signal versus guilt as a conditioned response.',
};

export function SISessionReportsPage() {
  const { isUnlocked } = useOutletContext<OutletCtx>();
  const { user } = useAuth();
  const [coachId, setCoachId] = useState<string | null>(null);
  const [clients, setClients] = useState<CoachingClient[]>([]);
  const [sessions, setSessions] = useState<CoachingSession[]>([]);
  const [reportsUsed, setReportsUsed] = useState(0);
  const [loadingData, setLoadingData] = useState(false);
  const [expandedSection, setExpandedSection] = useState<string | null>('Session Summary');
  const [intelligenceSession, setIntelligenceSession] = useState<CoachingSession | null>(null);
  const [panelClientName, setPanelClientName] = useState('');
  const [creatingFor, setCreatingFor] = useState<string | null>(null);
  const [error, setError] = useState('');
  const [showNewReport, setShowNewReport] = useState(false);

  useEffect(() => {
    if (!isUnlocked || !user) return;
    const load = async () => {
      setLoadingData(true);
      const { data: coach } = await supabase.from('coaches').select('id, si_reports_used').eq('user_id', user.id).maybeSingle();
      if (!coach) return;
      const coachRow = coach as unknown as { id: string; si_reports_used?: number };
      setCoachId(coachRow.id);
      setReportsUsed(coachRow.si_reports_used ?? 0);
      const [c, s] = await Promise.all([getClients(coachRow.id), getSessions(coachRow.id)]);
      setClients(c);
      setSessions(s);
      setLoadingData(false);
    };
    load();
  }, [isUnlocked, user]);

  const handleNewReport = async (client: CoachingClient) => {
    if (!coachId) return;
    setCreatingFor(client.id);
    try {
      const { data, error: err } = await createSession({
        client_id: client.id,
        coach_id: coachId,
        session_date: new Date().toISOString().slice(0, 10),
        duration_minutes: 60,
        session_type: 'video',
      });
      if (err) throw err;
      if (data) {
        const newSession = data as unknown as CoachingSession;
        setSessions(prev => [newSession, ...prev]);
        setPanelClientName(client.full_name);
        setIntelligenceSession(newSession);
        setShowNewReport(false);
      }
    } catch {
      setError('Failed to start analysis. Please try again.');
    } finally {
      setCreatingFor(null);
    }
  };

  const completedSessions = sessions.filter(s => s.analysis_status === 'complete');

  return (
    <div className="p-4 sm:p-6 lg:p-8 max-w-5xl mx-auto space-y-6">
      <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }}>
        <div className="flex items-start justify-between gap-4">
          <div>
            <h1 className="text-2xl font-bold text-gray-80">Session Reports</h1>
            <p className="text-sm text-gray-50 mt-1">
              Upload a coaching session audio file, transcript, or notes and receive a structured coaching insight report.
            </p>
          </div>
          {isUnlocked && (
            <button
              onClick={() => setShowNewReport(true)}
              disabled={reportsUsed >= 1}
              className="inline-flex items-center gap-2 px-4 py-2.5 bg-success-40 hover:bg-success-50 text-white rounded-2xl text-sm font-semibold transition-colors flex-shrink-0 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <Plus className="w-4 h-4" />
              New Report
            </button>
          )}
        </div>
      </motion.div>

      {error && (
        <div className="flex items-center gap-2 bg-red-50 text-red-700 p-4 rounded-2xl text-sm">
          <AlertCircle className="w-4 h-4 flex-shrink-0" />
          {error}
          <button onClick={() => setError('')} className="ml-auto"><X className="w-4 h-4" /></button>
        </div>
      )}

      {isUnlocked && reportsUsed >= 1 && (
        <div className="flex items-start gap-3 bg-amber-50 border border-amber-200 p-4 rounded-2xl text-sm">
          <AlertCircle className="w-4 h-4 text-amber-600 flex-shrink-0 mt-0.5" />
          <div>
            <p className="font-semibold text-amber-800">1 of 1 free report used</p>
            <p className="text-amber-700 text-xs mt-0.5">You have used your free Session Intelligence report. Contact us to upgrade and unlock unlimited reports.</p>
          </div>
        </div>
      )}

      {/* Pre-unlock CTA */}
      {!isUnlocked && (
        <motion.div
          initial={{ opacity: 0, y: 12 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.05 }}
          className="bg-white rounded-2xl border border-gray-20 p-6"
        >
          <div className="flex items-start gap-4">
            <div className="p-3 bg-gray-10 rounded-xl flex-shrink-0">
              <Lock className="w-5 h-5 text-gray-40" />
            </div>
            <div className="flex-1">
              <h2 className="text-base font-semibold text-gray-80 mb-1">Unlock Your First Session Intelligence™ Report</h2>
              <p className="text-sm text-gray-50 mb-4 leading-relaxed">
                Once activated, you'll be able to upload session audio, transcripts, or notes and receive a full structured coaching insight report — covering 14 analysis sections.
              </p>
              <a
                href={WALKTHROUGH_URL}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-2 px-4 py-2 bg-success-40 hover:bg-success-50 text-white rounded-xl text-sm font-semibold transition-colors"
              >
                Unlock Your First Report on the Live Walkthrough
                <ExternalLink className="w-3.5 h-3.5" />
              </a>
            </div>
          </div>
        </motion.div>
      )}

      {/* Upload options preview */}
      {!isUnlocked && (
        <motion.div
          initial={{ opacity: 0, y: 12 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.08 }}
          className="grid sm:grid-cols-3 gap-4"
        >
          {[
            { icon: Upload, label: 'Upload Audio', desc: 'MP3, M4A, WAV up to 200MB', locked: true },
            { icon: FileText, label: 'Upload Transcript or Document', desc: 'PDF, DOCX, TXT up to 50MB', locked: true },
            { icon: Brain, label: 'Session Notes Only', desc: 'Paste typed notes directly', locked: true },
          ].map(option => (
            <div key={option.label} className="bg-white rounded-2xl border border-gray-20 p-4 opacity-60">
              <div className="p-2.5 bg-gray-10 rounded-xl w-fit mb-3">
                <option.icon className="w-5 h-5 text-gray-40" />
              </div>
              <p className="text-sm font-semibold text-gray-80">{option.label}</p>
              <p className="text-xs text-gray-40 mt-0.5">{option.desc}</p>
              <div className="mt-3 flex items-center gap-1 text-xs text-gray-40">
                <Lock className="w-3 h-3" />
                Unlock on walkthrough
              </div>
            </div>
          ))}
        </motion.div>
      )}

      {/* Sample report preview */}
      <motion.div
        initial={{ opacity: 0, y: 12 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.12 }}
        className="bg-white rounded-2xl border border-gray-20 overflow-hidden"
      >
        <div className="px-5 py-4 border-b border-gray-10 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-8 h-8 rounded-full bg-success-40 flex items-center justify-center text-white text-xs font-bold">SA</div>
            <div>
              <p className="text-xs text-gray-40 font-medium">Sample Report Preview</p>
              <h3 className="text-sm font-bold text-gray-80">Sarah — Session 3</h3>
            </div>
          </div>
          {!isUnlocked && (
            <span className="inline-flex items-center px-2 py-0.5 rounded-full bg-orange-10 text-orange-70 text-xs font-semibold">Demo Preview</span>
          )}
        </div>
        <div className="divide-y divide-gray-10">
          {REPORT_SECTIONS.map(section => (
            <div key={section}>
              <button
                className="w-full px-5 py-3.5 flex items-center justify-between hover:bg-brand-5 transition-colors text-left"
                onClick={() => setExpandedSection(expandedSection === section ? null : section)}
              >
                <span className="text-sm font-medium text-gray-80">{section}</span>
                {expandedSection === section
                  ? <ChevronUp className="w-4 h-4 text-gray-40" />
                  : <ChevronDown className="w-4 h-4 text-gray-40" />
                }
              </button>
              <AnimatePresence>
                {expandedSection === section && (
                  <motion.div
                    initial={{ height: 0, opacity: 0 }}
                    animate={{ height: 'auto', opacity: 1 }}
                    exit={{ height: 0, opacity: 0 }}
                    transition={{ duration: 0.2 }}
                    className="overflow-hidden"
                  >
                    <div className="px-5 pb-4">
                      <p className="text-sm text-gray-60 leading-relaxed">
                        {SAMPLE_REPORT_PREVIEW[section] ?? 'Sample analysis content will appear here after your first session is uploaded and analysed. This section provides structured coaching insights derived from the client\'s language patterns, themes, and stated experiences.'}
                      </p>
                    </div>
                  </motion.div>
                )}
              </AnimatePresence>
            </div>
          ))}
        </div>
      </motion.div>

      {/* Existing reports (unlocked) */}
      {isUnlocked && (
        <div>
          {loadingData ? (
            <div className="flex items-center justify-center py-8">
              <div className="w-6 h-6 border-2 border-success-40 border-t-transparent rounded-full animate-spin" />
            </div>
          ) : completedSessions.length > 0 ? (
            <div>
              <h2 className="text-sm font-semibold text-gray-60 mb-3">Report History</h2>
              <div className="space-y-3">
                {completedSessions.map(session => {
                  const client = clients.find(c => c.id === session.client_id);
                  return (
                    <button
                      key={session.id}
                      onClick={() => {
                        if (client) { setPanelClientName(client.full_name); setIntelligenceSession(session); }
                      }}
                      className="w-full bg-white rounded-2xl border border-gray-20 px-5 py-4 flex items-center justify-between hover:bg-brand-5 transition-colors text-left"
                    >
                      <div className="flex items-center gap-3">
                        <div className="w-8 h-8 rounded-full bg-success-40 flex items-center justify-center text-white text-xs font-bold">
                          {client?.full_name.slice(0, 2).toUpperCase() ?? '?'}
                        </div>
                        <div>
                          <p className="text-sm font-semibold text-gray-80">{client?.full_name ?? 'Unknown'}</p>
                          <p className="text-xs text-gray-40">
                            {new Date(session.session_date).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' })}
                          </p>
                        </div>
                      </div>
                      <span className="text-xs text-success-50 font-medium">View Report</span>
                    </button>
                  );
                })}
              </div>
            </div>
          ) : null}
        </div>
      )}

      {/* New report modal — client selector */}
      <AnimatePresence>
        {showNewReport && isUnlocked && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4"
            onClick={e => { if (e.target === e.currentTarget) setShowNewReport(false); }}
          >
            <motion.div
              initial={{ scale: 0.95, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.95, opacity: 0 }}
              className="bg-white rounded-2xl shadow-soft-lg p-6 w-full max-w-md"
            >
              <div className="flex items-center justify-between mb-5">
                <h2 className="text-base font-semibold text-gray-80">Select a Client</h2>
                <button onClick={() => setShowNewReport(false)} className="p-1.5 hover:bg-gray-10 rounded-lg transition-colors">
                  <X className="w-4 h-4 text-gray-50" />
                </button>
              </div>
              {clients.length === 0 ? (
                <p className="text-sm text-gray-50 text-center py-4">No clients yet. Add a client first.</p>
              ) : (
                <div className="space-y-2">
                  {clients.map(client => (
                    <button
                      key={client.id}
                      onClick={() => handleNewReport(client)}
                      disabled={creatingFor === client.id}
                      className="w-full flex items-center gap-3 px-4 py-3 rounded-xl border border-gray-20 hover:bg-brand-5 hover:border-success-40 transition-all text-left"
                    >
                      <div className="w-8 h-8 rounded-full bg-success-40 flex items-center justify-center text-white text-xs font-bold flex-shrink-0">
                        {client.full_name.slice(0, 2).toUpperCase()}
                      </div>
                      <div className="flex-1">
                        <p className="text-sm font-semibold text-gray-80">{client.full_name}</p>
                        {client.email && <p className="text-xs text-gray-40">{client.email}</p>}
                      </div>
                      {creatingFor === client.id && (
                        <div className="w-4 h-4 border-2 border-success-40 border-t-transparent rounded-full animate-spin" />
                      )}
                    </button>
                  ))}
                </div>
              )}
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Session Intelligence Panel */}
      <AnimatePresence>
        {intelligenceSession && coachId && (
          <SessionIntelligencePanel
            session={sessions.find(s => s.id === intelligenceSession.id) || intelligenceSession}
            coachId={coachId}
            clientName={panelClientName}
            reportsUsed={reportsUsed}
            onClose={() => setIntelligenceSession(null)}
            onUpdate={(sessionId, updates) => {
              setSessions(prev => prev.map(s => s.id === sessionId ? { ...s, ...updates } : s));
            }}
            onReportUsed={() => setReportsUsed(r => r + 1)}
          />
        )}
      </AnimatePresence>
    </div>
  );
}
